/**
 * <ReferralInviteBanner> — l'invitation au parrainage, en bas de l'accueil parent (molecule).
 *
 * Elle remplace le cœur du header (Wajih, 28/09 : « je préfère le bandeau en bas plutôt que le
 * cœur en haut »), et elle doit donner envie de cliquer sans devenir un second temps fort : sur
 * l'accueil, la seule surface forte est la carte encadrée de la prochaine séance. D'où un
 * bandeau et pas une carte de plus :
 *  - **la lavande de la marque en fond** (`primary-soft`, `#2A3862` sur la nuit), au lieu du blanc
 *    des cartes — pas d'aplat marine, pas de dégradé, pas d'aurore, qui ont chacun leur sens ;
 *  - **le cœur dans une pastille blanche**, le glyphe de l'entrée qu'il remplace ;
 *  - **le gain de la famille qui parraine d'abord, en gras** (« 50 € pour vous dès qu'elle
 *    s'abonne »), puis celui de la famille invitée ;
 *  - **un vrai bouton**, « Inviter une famille » : c'est lui qui presse, la surface ne presse
 *    pas — un bouton dans un bouton ne s'annonce pas.
 *
 * Le montant arrive déjà écrit (`rewardLabel`, « 50 € », comme sur la page de parrainage) ;
 * ABSENT, la phrase se passe de montant plutôt que d'en supposer un. Rien n'y parle de l'enfant :
 * on invite une famille, on ne promet rien sur celle qui invite.
 *
 * La disposition se lit sur la largeur du bandeau, pas sur la fenêtre : le bouton passe à droite
 * dès que le texte a la place de tenir à côté, et sous le texte, pleine largeur, sinon. Elle ne
 * se place pas elle-même : c'est la page qui la pose, et seulement quand le geste existe.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { Card } from "../Card"

export type ReferralInviteBannerProps = {
    /** « 50 € » — ce que le parrain gagne, écrit comme la page. Absent ⇒ phrase sans montant. */
    rewardLabel?: string
    /** Ouvre la page de parrainage. */
    onPress: () => void
    testID?: string
}

/**
 * Largeur intérieure à partir de laquelle le bouton se range à droite du texte : la pastille, un
 * titre qui tient sur sa ligne et le bouton, sans que la phrase ne passe sur trois lignes.
 */
const BESIDE_FROM = 520

export function ReferralInviteBanner(props: ReferralInviteBannerProps) {
    const { rewardLabel, onPress, testID } = props
    const { onLayout, atLeast: beside } = useWidthAtLeast(BESIDE_FROM)

    const hasReward = rewardLabel != null && rewardLabel !== ""

    return (
        <Card background="primary-soft" p="4" testID={testID}>
            <View onLayout={onLayout} style={styles.layout(beside)}>
                <Row gap="3" align="center" style={styles.body}>
                    <IconBubble iconName="heart" size={48} color="surface-raised" iconColor="primary" />
                    <Stack gap="1" style={styles.body}>
                        <Text size="text-xl" weight="bold">
                            Parrainer une famille
                        </Text>
                        <Text size="text-md" color="text-muted">
                            {hasReward ? (
                                <>
                                    <Text size="text-md" weight="bold">
                                        {`${rewardLabel} pour vous`}
                                    </Text>
                                    {" dès qu'elle s'abonne, une séance découverte offerte pour elle."}
                                </>
                            ) : (
                                "Une séance découverte offerte pour elle."
                            )}
                        </Text>
                    </Stack>
                </Row>
                <Button label="Inviter une famille" onPress={onPress} fullWidth={!beside} />
            </View>
        </Card>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** En colonne sur un téléphone (le bouton sous le texte), en rangée dès que la place le permet. */
    layout: (beside: boolean) => ({
        flexDirection: beside ? ("row" as const) : ("column" as const),
        alignItems: beside ? ("center" as const) : ("stretch" as const),
        gap: beside ? theme.spacing["6"] : theme.spacing["4"],
    }),
    /** Le texte prend la place qui reste et passe à la ligne ; pastille et bouton gardent leur taille. */
    body: { flexShrink: 1, flexGrow: 1 },
}))
